import { SiteHeader } from '../components/SiteHeader'
import { Footer } from '../components/Footer'
import { Button } from '../components/Button'
import { MediaFrame } from '../components/MediaFrame'
import { GestureMap } from '../components/GestureMap'
import { GestureVocabulary } from '../components/GestureVocabulary'
import { PlainEnglish } from '../components/PlainEnglish'
import { SplitSection } from '../components/SplitSection'
import { Reveal } from '../components/Reveal'
import { SITE } from '../config/site'
import { MEDIA } from '../config/media'
import styles from './HomePage.module.css'

/** The three beats of the window-control demonstration. */
const BEATS = [
  { kicker: 'Gesture', text: 'Four fingers, one swipe. Left, right, up or down.' },
  { kicker: 'Preview', text: 'The destination fills in live as you move.' },
  { kicker: 'Snap', text: 'Let go. The window lands where the preview showed.' },
]

/** Purchase facts, kept as discrete items rather than one grey run-on line. */
const SPECS = [
  '14-day trial in the app',
  `${SITE.priceLabel} one-time`,
  'No subscription',
  'Up to 2 personal Macs',
  'macOS 14 or later',
]

const PURCHASE_FAQ = [
  {
    question: 'Is Gesturely a subscription?',
    answer: 'No. Gesturely is $14.99 USD once, with no recurring subscription.',
  },
  {
    question: 'Can I try it before buying?',
    answer: 'Yes. The app includes a 14-day full-feature trial before purchase.',
  },
  {
    question: 'Does it need an account or cloud connection?',
    answer:
      'No account is required for local Gesturely use. Plain-English actions are resolved on your Mac without a cloud LLM or API key. Purchases, licensing, email delivery, and updates may use network services.',
  },
  {
    question: 'How many Macs does one purchase cover?',
    answer:
      'One personal license covers up to two Macs. Restore Purchase is available from Gesturely Settings.',
  },
  {
    question: 'Who handles payment and support?',
    answer:
      'Payments are processed by Paddle, our Merchant of Record. Product support is available at support@getgesturely.com.',
  },
  {
    question: 'What does Gesturely require?',
    answer: SITE.systemRequirement,
  },
]

/** Exact aspect ratio of the new 1920 × 1242 screen-recording derivatives. */
const CAPTURE_RATIO = '320 / 207'

export function HomePage() {
  return (
    <div className={styles.page}>
      <SiteHeader />

      <main id="main-content">
        {/* ═══ Hero ═══════════════════════════════════════════ */}
        <section className={styles.hero}>
          <div className={styles.heroAmbient} aria-hidden="true" />

          <Reveal className={`container ${styles.heroInner}`}>
            <p className={styles.heroEyebrow}>Native macOS menu-bar app</p>

            <h1 className={styles.heroTitle}>
              <span className={styles.heroLine}>Your trackpad,</span>
              <span className={`${styles.heroLine} ${styles.heroAccent}`}>
                programmable.
              </span>
            </h1>

            <p className={styles.heroLede}>
              Gesturely turns multi-finger swipes, taps, firm presses, and drawn
              shapes into actions on your Mac. Move windows, open apps, and create
              gestures around the way you work. Describe what you want in plain
              English, and Gesturely resolves it on your Mac.
            </p>

            <div className={styles.heroActions}>
              <Button href={SITE.downloadUrl} variant="primary" size="lg">
                Download for Mac
              </Button>
              <Button to="/buy" variant="secondary" size="lg">
                Buy {SITE.productName}
              </Button>
            </div>

            <ul className={styles.heroSpecs}>
              {SPECS.map((spec) => (
                <li key={spec} className={styles.heroSpec}>
                  {spec}
                </li>
              ))}
            </ul>
          </Reveal>
        </section>

        {/* ═══ Opening stage: the product, immediately ════════ */}
        <section id="how-it-works" className={styles.stageSection}>
          <Reveal className={`container container--wide ${styles.stage}`}>
            <MediaFrame
              priority
              clip={MEDIA.windowSnap}
              description="Safari moves through left, right, corner, and maximized placements, showing how one window can be sent exactly where it is needed."
              aspectRatio={CAPTURE_RATIO}
            />
          </Reveal>

          {/* Caption band: title left, the three beats right. */}
          <div className={`container container--wide ${styles.beatBand}`}>
            <Reveal className={styles.beatIntro}>
              <h2 className={styles.beatTitle}>Swipe. Preview. Snap.</h2>
              <p className={styles.beatCopy}>
                A four-finger swipe moves the window. Gesturely shows the
                destination while you move, so you let go exactly where you want it.
              </p>
            </Reveal>

            <ol className={styles.beats}>
              {BEATS.map((beat, i) => (
                <li key={beat.kicker} className={styles.beat}>
                  <Reveal className={styles.beatBody} delayMs={i * 90}>
                    <span className={styles.beatKicker}>{beat.kicker}</span>
                    <span className={styles.beatText}>{beat.text}</span>
                  </Reveal>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ═══ Live preview ══════════════════════════════════ */}
        <section id="preview" className={styles.preview}>
          <div className={`container container--wide ${styles.previewGrid}`}>
            <Reveal className={styles.previewMedia}>
              <MediaFrame
                clip={MEDIA.zonePreview}
                description="During several window gestures, a translucent labeled panel previews each destination before Safari lands there."
                aspectRatio={CAPTURE_RATIO}
              />
            </Reveal>

            <Reveal className={styles.previewCopy} delayMs={90}>
              <h2 className={styles.previewTitle}>
                See where it lands before you let go.
              </h2>
              <p className={styles.previewText}>
                Gesturely shows the destination while your fingers are still moving.
                Nothing is decided after the fact. Release when the placement looks
                right, and a light haptic tap marks the snap.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ═══ Defaults ══════════════════════════════════════ */}
        <section id="defaults" className={styles.defaults}>
          <div className={`container ${styles.defaultsInner}`}>
            <Reveal className={styles.centeredHead}>
              <h2 className={styles.sectionTitle}>Useful from the first launch.</h2>
              <p className={styles.sectionCopy}>
                Halves, maximize, minimize, full screen, and movement between
                displays. Seven defaults that do something useful the moment
                Gesturely opens, and every one of them is yours to change.
              </p>
            </Reveal>

            <Reveal className={styles.defaultsMap} delayMs={90}>
              <GestureMap />
            </Reveal>

            <Reveal className={styles.defaultsClip} delayMs={130}>
              <MediaFrame
                clip={MEDIA.windowGrids}
                description="Gesturely previews the available left, right, corner, maximize, and minimize window destinations around Safari."
                caption="Window positions in motion. Halves, corners, maximize, and minimize stay available to assign."
                aspectRatio={CAPTURE_RATIO}
              />
            </Reveal>

            <Reveal className={styles.defaultsClip} delayMs={170}>
              <MediaFrame
                clip={MEDIA.fullScreen}
                description="Safari enters native Full Screen, then the same gesture returns it to windowed mode."
                caption="Full Screen toggles both ways. Factory default: three-finger double-tap."
                aspectRatio={CAPTURE_RATIO}
              />
            </Reveal>
          </div>
        </section>

        {/* ═══ The pause ═════════════════════════════════════ */}
        <section id="gestures" className={styles.turn}>
          <div className={`container ${styles.turnInner}`}>
            <Reveal>
              <h2 className={styles.turnTitle}>
                <span className={styles.turnLine}>It starts with windows.</span>
                <span className={`${styles.turnLine} ${styles.turnMuted}`}>
                  It doesn&rsquo;t end there.
                </span>
              </h2>
            </Reveal>

            <Reveal delayMs={120}>
              <p className={styles.turnCopy}>
                Draw an <kbd className={styles.kbd}>S</kbd> to open Spotify. Firm
                press to mute. Build a five-finger tap that opens Safari. Window
                control is only the beginning. Gesturely lets you shape the trackpad
                around the way you use your Mac.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ═══ Vocabulary ════════════════════════════════════ */}
        <section id="vocabulary" className={styles.vocabulary}>
          <div className={`container container--wide ${styles.vocabularyInner}`}>
            <Reveal className={styles.vocabularyHead}>
              <p className={styles.eyebrow}>The vocabulary</p>
              <h2 className={styles.vocabularyTitle}>
                Swipes, taps, a firm press, a shape.
              </h2>
              <p className={styles.vocabularyCopy}>
                Those are the raw materials. Point any of them at an action, keep the
                ones that stick, and change the rest when your habits change.
              </p>
            </Reveal>

            <Reveal delayMs={90}>
              <GestureVocabulary />
            </Reveal>
          </div>
        </section>

        {/* ═══ Drawn shapes ══════════════════════════════════ */}
        <SplitSection
          id="shapes"
          eyebrow="Drawn shapes"
          title="Draw an S. Spotify opens."
          footnote="A letter for an app. A loop for a layout."
          media={
            <MediaFrame
              clip={MEDIA.drawnShape}
              description="Gesturely recognizes a real S shape, then the same assigned gesture is performed on the desktop and Spotify opens."
              aspectRatio={CAPTURE_RATIO}
            />
          }
        >
          <p>
            Hold one finger on the trackpad and draw a shape. Gesturely matches what
            you drew and runs the action you gave it.
          </p>
          <p>Whatever you will actually remember.</p>
        </SplitSection>

        {/* ═══ Apps and links: the widest stage ══════════════ */}
        <section id="launch" className={styles.launch}>
          <div className={`container ${styles.launchHead}`}>
            <Reveal className={styles.centeredHead}>
              <p className={styles.eyebrow}>Apps and links</p>
              <h2 className={styles.sectionTitle}>
                One motion for the app you live in.
              </h2>
              <p className={styles.sectionCopy}>
                Choose a five-finger tap, write “open Safari,” and save it. From then
                on, that one motion opens the app. You can point another gesture at a
                folder or a URL you check every morning.
              </p>
            </Reveal>
          </div>

          <Reveal
            className={`container container--wide ${styles.stage} ${styles.launchStage}`}
            delayMs={90}
          >
            <MediaFrame
              clip={MEDIA.appLaunch}
              description="Gesturely creates a five-finger tap, resolves the typed instruction open Safari, saves the mapping, and then launches Safari when the gesture is performed."
              aspectRatio={CAPTURE_RATIO}
            />
          </Reveal>
        </section>

        {/* ═══ Firm press ════════════════════════════════════ */}
        <SplitSection
          id="firm-press"
          eyebrow="Firm press"
          title="Press a little harder."
          mediaLeft
          footnote="A press you can already feel, doing something you chose."
          media={
            <MediaFrame
              clip={MEDIA.firmPress}
              description="A Firm Press gesture is calibrated, assigned the plain-English action mute volume, saved, and then performed repeatedly to mute and unmute the Mac."
              aspectRatio={CAPTURE_RATIO}
            />
          }
        >
          <p>
            A firm press on the trackpad is a gesture of its own. Send it to mute, to
            a volume step, or to anything else in your list.
          </p>
        </SplitSection>

        {/* ═══ Plain English ═════════════════════════════════ */}
        <section id="plain-english" className={styles.plain}>
          <div className={`container container--wide ${styles.plainInner}`}>
            <Reveal className={styles.plainHead}>
              <p className={styles.eyebrow}>Plain English</p>
              <h2 className={styles.plainTitle}>
                <span className={styles.plainLine}>Say what you want.</span>
                <span className={styles.plainLine}>It works out the rest.</span>
              </h2>
              <p className={styles.plainCopy}>
                Assigning an action is not a trip through nested menus. Write it the
                way you would say it, and Gesturely turns the sentence into the
                action.
              </p>
            </Reveal>

            <Reveal className={styles.plainBody} delayMs={90}>
              <PlainEnglish />
            </Reveal>
          </div>
        </section>

        {/* ═══ On device: the quiet one ══════════════════════ */}
        <section id="on-device" className={styles.onDevice}>
          <div className={`container ${styles.onDeviceInner}`}>
            <Reveal className={styles.onDeviceBody}>
              <span className={styles.onDeviceRule} aria-hidden="true" />
              <h2 className={styles.onDeviceTitle}>
                <span className={styles.onDeviceLine}>Plain English stays</span>
                <span className={styles.onDeviceLine}>on your Mac.</span>
              </h2>
              <p className={styles.onDeviceCopy}>
                Plain-English actions are resolved on-device, without a cloud LLM or
                API key. No account is required for local Gesturely use. Purchases,
                licensing, email delivery, and software updates may still use network
                services.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ═══ Gesture Tester ════════════════════════════════ */}
        <SplitSection
          id="tester"
          eyebrow="Gesture Tester"
          title="See exactly what your Mac sees."
          stackEarly
          footnote="Reliable first. Mapped second."
          media={
            <MediaFrame
              clip={MEDIA.gestureTester}
              description="The Gesture Tester recognizes Left Half, Right Half, and Maximize in sequence and leaves all three successful results visible."
              aspectRatio={CAPTURE_RATIO}
            />
          }
        >
          <p>
            Before you assign anything, run the gesture through the Gesture Tester. It
            shows you what Gesturely recognized, so you can adjust the motion until it
            fires every time.
          </p>
        </SplitSection>

        {/* ═══ Pre-purchase answers ═════════════════════════ */}
        <section id="faq" className={styles.faq} aria-labelledby="faq-title">
          <div className={`container container--wide ${styles.faqInner}`}>
            <Reveal className={styles.faqHead}>
              <p className={styles.eyebrow}>Before you buy</p>
              <h2 id="faq-title" className={styles.faqTitle}>
                Know exactly what you&rsquo;re getting.
              </h2>
              <p className={styles.faqIntro}>
                The practical details, without a subscription pitch or an account
                requirement.
              </p>
            </Reveal>

            <dl className={styles.faqList}>
              {PURCHASE_FAQ.map((item, index) => (
                <Reveal key={item.question} className={styles.faqItem} delayMs={index * 55}>
                  <dt className={styles.faqQuestion}>{item.question}</dt>
                  <dd className={styles.faqAnswer}>{item.answer}</dd>
                </Reveal>
              ))}
            </dl>
          </div>
        </section>

        {/* ═══ Pricing: the conclusion ═══════════════════════ */}
        <section id="pricing" className={styles.pricing}>
          <div className={styles.pricingAmbient} aria-hidden="true" />

          <div className={`container ${styles.pricingInner}`}>
            <Reveal className={styles.pricingHead}>
              <p className={styles.eyebrow}>One-time purchase</p>
              <p className={styles.pricingPrice}>
                <span className={styles.pricingAmount}>{SITE.priceLabel}</span>
                <span className={styles.pricingOnce}>, once.</span>
              </p>
              <p className={styles.pricingCopy}>
                No subscription. The license covers up to two personal Macs, and the
                14-day full-feature trial lives in the app itself.
              </p>
            </Reveal>

            <Reveal className={styles.pricingActions} delayMs={100}>
              <div className={styles.heroActions}>
                <Button href={SITE.downloadUrl} variant="primary" size="lg">
                  Download for Mac
                </Button>
                <Button to="/buy" variant="secondary" size="lg">
                  Buy {SITE.productName}
                </Button>
              </div>
              <div className={styles.pricingNotes}>
                <p className={styles.pricingFootnote}>{SITE.systemRequirement}</p>
                <p className={styles.pricingFootnote}>
                  Secure checkout by Paddle, our Merchant of Record.
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
